/**
 * useUiPreferencesStore — UI偏好设置（持久化）
 *
 * 管理用户界面行为开关，持久化到 localStorage。
 * 当前管理：
 * - tooltipsEnabled: 全局Tooltip开关（控制所有tooltip机制）
 *
 * 原先这里还有一个沉浸模式的全局偏好字段，**全仓零消费**：真正在用的两个视图各持自己的 `ref`
 * （画布=会话级不持久化；PureNexus=读自己的 `omni-immersive-mode`）。那个"看起来像全局偏好、
 * 实际没人读"的字段是**陷阱** —— 本轮照它去改、去重载，白跑一轮才发现没效果。已删除；
 * "禅模式要不要做成全局持久偏好"属产品决策，另登记。
 */

import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

const STORAGE_KEY = 'omni_ui_preferences';

interface PersistedPrefs {
    tooltipsEnabled: boolean;
}

function loadPrefs(): Partial<PersistedPrefs> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
    } catch {
        // ignore parse errors
    }
    return {};
}

export const useUiPreferencesStore = defineStore('uiPreferences', () => {
    const loaded = loadPrefs();

    const tooltipsEnabled = ref(loaded.tooltipsEnabled ?? true);

    // Persist on change
    watch(
        [tooltipsEnabled],
        () => {
            const prefs: PersistedPrefs = {
                tooltipsEnabled: tooltipsEnabled.value,
            };
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
            } catch {
                // ignore storage errors
            }
        },
        { deep: true }
    );

    function toggleTooltips() {
        tooltipsEnabled.value = !tooltipsEnabled.value;
    }

    return {
        tooltipsEnabled,
        toggleTooltips,
    };
});
